import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import Checkbox from "@vahak/core-ui/dist/components/Checkbox";
import LineDivider from "@vahak/core-ui/dist/components/LineDivider";
import { VerticalFlex } from "@vahak/core-ui/dist/layout/Flex";
import Radio from "@vahak/core-ui/dist/components/Radio";
import { SelectOption } from "@vahak/core-ui/dist/components/Select";

import QuickActionPopup from "../../../../quick-action-popup/QuickActionPopup";
import { isAnyDestinationSelected, MARKETPLACE_ANY_DESTINATION_OPTION } from "./marketplaceFilterStateOptions";
import Styles from "./MarketplaceFilterMobileSheet.module.scss";

interface MarketplaceFilterMobileSheetProps {
    title: string;
    options: SelectOption<number>[];
    value: SelectOption<number>[];
    isMulti?: boolean;
    onChange: (value: SelectOption<number>[]) => void;
    onClose: () => void;
}

const MarketplaceFilterMobileSheet = ({
    title,
    options,
    value,
    isMulti = false,
    onChange,
    onClose
}: MarketplaceFilterMobileSheetProps) => {
    const [localValues, setLocalValues] = useState<SelectOption<number>[]>(value);
    const [isMounted, setIsMounted] = useState(false);
    const isLocalAny = isAnyDestinationSelected(localValues);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        setLocalValues(value);
    }, [value]);

    const selectCheckBox = (option: SelectOption<number>) => {
        if (option.id === MARKETPLACE_ANY_DESTINATION_OPTION.id) {
            setLocalValues([]);
            return;
        }

        setLocalValues((current) => [
            ...current.filter((item) => item.id !== MARKETPLACE_ANY_DESTINATION_OPTION.id),
            option
        ]);
    };

    const deselectCheckBox = (option: SelectOption<number>) => {
        setLocalValues((current) => current.filter((item) => item.id !== option.id));
    };

    const onApply = () => {
        onChange(localValues);
        onClose();
    };

    if (!isMounted) {
        return null;
    }

    return createPortal(
        <QuickActionPopup
            popupProps={{ fullScreenSheet: true }}
            modalProps={{ modalBodyHeight: "60vh" }}
            modalSize="fit"
            isOpen
            titleText={title}
            onToggle={onClose}
            ctaAction={onApply}
            ctaText="Apply"
            ctaPlacement="footer"
            commonCtaProps={{ isRounded: true, blockBtn: true }}
            secondaryCtaText="Clear selection"
            secondaryCtaProps={{
                onClick: () => {
                    onChange([]);
                    onClose();
                },
                disabled: localValues.length === 0 && isLocalAny
            }}
            description={
                <div className={Styles.sheet}>
                    {isMulti ? (
                        <VerticalFlex gap={12}>
                            {options.map((option, index, arr) => {
                                const isAnyOption = option.id === MARKETPLACE_ANY_DESTINATION_OPTION.id;
                                const isChecked = isAnyOption
                                    ? isLocalAny
                                    : Boolean(localValues.find((item) => item.id === option.id));

                                const handleToggle = () => {
                                    if (isChecked) {
                                        deselectCheckBox(option);
                                    } else {
                                        selectCheckBox(option);
                                    }
                                };

                                return (
                                    <React.Fragment key={option.id}>
                                        <button type="button" className={Styles.rowButton} onClick={handleToggle}>
                                            <Checkbox
                                                size="md"
                                                id={String(option.id)}
                                                label={option.label}
                                                name={String(option.id)}
                                                onChange={handleToggle}
                                                value={isChecked}
                                                checkboxWrapperClass={Styles.checkboxRow}
                                                checkBoxLabelClass={Styles.checkboxLabel}
                                            />
                                        </button>
                                        {index < arr.length - 1 ? <LineDivider styleType="solid" width="100%" /> : null}
                                    </React.Fragment>
                                );
                            })}
                        </VerticalFlex>
                    ) : (
                        <VerticalFlex gap={0}>
                            {options.map((option, index, arr) => {
                                const isChecked = Boolean(value.find((item) => item.id === option.id));

                                return (
                                    <React.Fragment key={option.id}>
                                        <div className={Styles.radioRow}>
                                            <Radio
                                                label={option.label}
                                                name={option.label}
                                                id={option.label}
                                                onChange={() => {
                                                    onChange([option]);
                                                    onClose();
                                                }}
                                                value={String(value[0]?.value || "")}
                                                checked={isChecked}
                                                diskPosition="end"
                                            />
                                        </div>
                                        {index < arr.length - 1 ? <LineDivider styleType="solid" width="100%" /> : null}
                                    </React.Fragment>
                                );
                            })}
                        </VerticalFlex>
                    )}
                </div>
            }
        />,
        document.body
    );
};

export default MarketplaceFilterMobileSheet;
